import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Button — Uber-inspired.
 * - default: negro sólido, texto blanco, tracking cerrado, sin sombra.
 * - outline: borde negro, texto negro, fondo transparente.
 * - ghost: sin borde, hover sutil.
 * - danger: rojo sólido para acciones destructivas confirmadas.
 *
 * Sizes optimizados para touch (tablet POS): md=44px, lg=56px, xl=64px.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold tracking-tight transition-colors ' +
    'disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ' +
    'rounded-md',
  {
    variants: {
      variant: {
        default: 'bg-fg text-fg-inverse hover:bg-fg-muted active:bg-fg',
        outline:
          'border border-border-strong bg-transparent text-fg hover:bg-surface-muted active:bg-surface-muted',
        ghost: 'bg-transparent text-fg hover:bg-surface-muted',
        danger: 'bg-danger text-white hover:opacity-90',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4 text-sm',
        lg: 'h-14 px-6 text-base',
        xl: 'h-16 px-8 text-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
  },
);
Button.displayName = 'Button';

export { buttonVariants };
